import type {
  MarkdownMessageEditRequest,
  NpMessageTarget,
} from './markdown-message';

import {
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Input,
  NativeSelect,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { CenteredDialogContent } from '#components/ui/centered-dialog-content';
import { toaster } from '#components/ui/toaster';

import { NP_MESSAGE_TAG } from './markdown-message';

export interface MarkdownMessageEditModalProps {
  request: MarkdownMessageEditRequest | null;
  onDismiss: () => void;
}

const TARGET_OPTIONS: Array<{ label: string; value: NpMessageTarget }> = [
  { label: 'Chat (dashboard filtered)', value: 'all' },
  { label: 'All messages', value: 'raw' },
  { label: 'This document', value: 'document' },
  { label: 'Single message thread', value: 'message' },
];

export function MarkdownMessageEditModal({
  request,
  onDismiss,
}: MarkdownMessageEditModalProps) {
  const open = request != null;
  const [target, setTarget] = useState<NpMessageTarget>('document');
  const [messageId, setMessageId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!request) {
      setSaving(false);
      return;
    }

    setTarget(request.initialValues.target);
    setMessageId(request.initialValues.messageId);
  }, [request]);

  const handleClose = () => {
    if (saving) {
      return;
    }

    onDismiss();
  };

  const handleSave = async () => {
    if (!request) {
      return;
    }

    const trimmedMessageId = messageId.trim();
    if (target === 'message' && !trimmedMessageId) {
      toaster.create({
        title: 'Message ID required',
        description:
          'Enter the root or child message id for the pinned thread.',
        type: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      await request.onCommit({
        target,
        messageId: trimmedMessageId,
      });
      onDismiss();
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(details) => {
        if (!details.open) {
          handleClose();
        }
      }}
    >
      <CenteredDialogContent>
        <DialogHeader>
          <DialogTitle>
            {request?.mode === 'insert' ? 'Insert messages' : 'Edit messages'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text fontSize="sm" color="fg.muted">
              Configure the embedded{' '}
              <Box as="code" display="inline">
                {`<${NP_MESSAGE_TAG}>`}
              </Box>{' '}
              block. JSON is stored inside the tag.
            </Text>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Target
              </Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={target}
                  onChange={(event) =>
                    setTarget(event.currentTarget.value as NpMessageTarget)
                  }
                >
                  {TARGET_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect.Field>
              </NativeSelect.Root>
            </Box>
            {target === 'message' ? (
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Message ID
                </Text>
                <Input
                  size="sm"
                  value={messageId}
                  onChange={(event) => setMessageId(event.currentTarget.value)}
                  placeholder="Root or reply message id"
                />
              </Box>
            ) : null}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            colorPalette="blue"
            onClick={() => void handleSave()}
            loading={saving}
          >
            Save
          </Button>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
